/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  Dispatch,
  SetStateAction,
  KeyboardEvent,
  ClipboardEvent,
  ChangeEvent,
} from "react";
import { useLocationBarcodeVerifyMutation } from "../../../hooks/inventoryHooks";
import toast from "react-hot-toast";
import type { UseFormSetValue } from "react-hook-form";

// --- PROPS TYPE ---
type AssignModalProps = {
  setValue: UseFormSetValue<any>; // From react-hook-form
  awbText: string;
  setAwbText: Dispatch<SetStateAction<string>>;
  locationId: string;
  setLocationId: Dispatch<SetStateAction<string>>;
};

export const useBarcodeScanner = (onScan: (code: string) => void) => {
  const onScanCallback = useRef(onScan);
  const isPasting = useRef(false);

  useEffect(() => {
    onScanCallback.current = onScan;
  }, [onScan]);

  useEffect(() => {
    let buffer = "";
    let lastKeyTime = 0;
    let timeout: NodeJS.Timeout | null = null;

    // --- Global paste handler to block scanner briefly ---
    const handlePaste = () => {
      isPasting.current = true;
      setTimeout(() => {
        isPasting.current = false;
      }, 150); // Block for 150ms after paste
    };

    // Attach to document (captures paste in any input/textarea)
    document.addEventListener("paste", handlePaste);

    const handleKeydown = (e: KeyboardEvent) => {
      const now = Date.now();

      // --- BLOCK ALL KEYS DURING PASTE WINDOW ---
      if (isPasting.current) {
        return;
      }

      // --- PASTE SHORTCUT: Ctrl+V or Cmd+V → IGNORE ---
      if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        return;
      }

      // --- ENTER: Submit buffer ---
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (buffer.length > 0) {
          onScanCallback.current(buffer.trim());
        }
        buffer = "";
        if (timeout) clearTimeout(timeout);
        return;
      }

      // --- Printable ASCII keys ---
      if (
        /^[\x20-\x7E]$/.test(e.key) &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        if (now - lastKeyTime > 200) buffer = "";
        buffer += e.key;
        lastKeyTime = now;

        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          if (buffer.length > 0) {
            onScanCallback.current(buffer.trim());
          }
          buffer = "";
        }, 500);
      }
    };

    window.addEventListener("keydown", handleKeydown as any, true);

    return () => {
      window.removeEventListener("keydown", handleKeydown as any, true);
      document.removeEventListener("paste", handlePaste);
      if (timeout) clearTimeout(timeout);
    };
  }, []);
};

// --- MAIN COMPONENT ---
const AddInventoryModal: React.FC<AssignModalProps> = ({
  setValue,
  awbText,
  setAwbText,
  setLocationId,
}) => {
  const [isShelfVerified, setIsShelfVerified] = useState(false);
  const [shelfId, setShelfId] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [shelfManualInput, setShelfManualInput] = useState<string>("");

  const shelfInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);

  const { mutateAsync: verifyLocationBarcode } = useLocationBarcodeVerifyMutation();

  // --- Reset Shelf ---
  const resetShelf = useCallback(() => {
    setShelfId("");
    setLocationId("");
    setIsShelfVerified(false);
    setAwbText("");
    setShelfManualInput("");
    setValue("targetShelfId", "");
    setValue("awbs", []);
    setTimeout(() => shelfInputRef.current?.focus(), 0);
  }, [setLocationId, setAwbText, setValue]);

  // --- Sync AWBs to Form (debounced, safe) ---
  useEffect(() => {
    const awbArray = awbText
      .split(/[\s,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    setValue("awbs", awbArray, { shouldDirty: true });
  }, [awbText, setValue]);

  const totalAwbs = awbText.split(/[\s,\n]+/).filter(Boolean).length;

  // --- Verify Shelf Barcode ---
  const handleVerifyBarcode = useCallback(
    async (barcode: string): Promise<boolean> => {
      const trimmed = barcode.trim();
      if (!trimmed) return false;

      try {
        const res = await verifyLocationBarcode({ barcodeId: trimmed });
        const locationId = typeof res === "string" ? res : String(res);

        setShelfId(locationId);
        setLocationId(trimmed);
        setIsShelfVerified(true);
        setShelfManualInput("");
        setValue("targetShelfId", trimmed);
        toast.success("Shelf verified!");
        return true;
      } catch (error: any) {
        toast.error(error.message || "Invalid shelf barcode");
        return false;
      }
    },
    [verifyLocationBarcode, setLocationId, setValue]
  );

  // --- Barcode Scanner Handler (Stable with useCallback + ref) ---
  const handleScannedBarcode = useCallback(
    async (barcode: string) => {
      if (processingRef.current) return;
      processingRef.current = true;
      setIsProcessing(true);

      try {
        if (!isShelfVerified) {
          await handleVerifyBarcode(barcode);
        } else {
          setAwbText((prev) => (prev ? `${prev}\n${barcode}` : barcode));
          toast.success(`AWB scanned: ${barcode}`);
        }
      } finally {
        processingRef.current = false;
        setIsProcessing(false);
      }
    },
    [isShelfVerified, handleVerifyBarcode]
  );

  // --- Attach Scanner ---
  useBarcodeScanner(handleScannedBarcode);

  // --- Manual Shelf Input ---
  const handleShelfKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && shelfManualInput.trim()) {
      e.preventDefault();
      setIsProcessing(true);
      await handleVerifyBarcode(shelfManualInput.trim());
      setIsProcessing(false);
    }
  };

  const handleShelfPaste = async (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim().split(/[\s,\n]+/, 1)[0];
    if (!pasted) return;

    setShelfManualInput(pasted);
    setIsProcessing(true);
    await handleVerifyBarcode(pasted);
    setIsProcessing(false);
  };

  const handleShelfChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShelfManualInput(e.target.value);
  };

  // --- AWB Paste ---
const handleAwbPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
  if (!isShelfVerified) {
    toast.error("Verify shelf first");
    e.preventDefault();
    return;
  }

  e.preventDefault();

  const pasted = e.clipboardData.getData("text");

  const newAwbs = pasted
    .split(/[\s,\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (newAwbs.length === 0) return;

  // ✅ Pehle current state se existingAwbs lo
  const existingAwbs = awbText
    ? awbText.split(/[\s,\n]+/).map((s) => s.trim())
    : [];

  const uniqueNewAwbs = newAwbs.filter(
    (awb) => !existingAwbs.includes(awb)
  );

  const skipped = newAwbs.length - uniqueNewAwbs.length;

  // ✅ Sirf ek toast
  if (uniqueNewAwbs.length === 0) {
    toast.error("All AWBs are duplicates, nothing added");
    return;
  }

  if (skipped > 0) {
    toast.error(`${skipped} duplicate(s) skipped`);
  }

  toast.success(`${uniqueNewAwbs.length} AWB(s) added`);

  // ✅ Sirf ek baar state update
  setAwbText((prev) =>
    prev ? `${prev}\n${uniqueNewAwbs.join("\n")}` : uniqueNewAwbs.join("\n")
  );
};


  const handleAwbChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAwbText(e.target.value);
  };

  // --- Auto-focus on mount ---
  useEffect(() => {
    shelfInputRef.current?.focus();
  }, []); 

  return (
    <div className="w-full py-4 px-6 space-y-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
      <div className="h-[calc(100vh-13rem)] overflow-y-auto space-y-4">
        {/* Shelf Section */}
        <div className="bg-[#FCFDF8] rounded-xl p-3 border border-[#D2D2D2]">
          <div className="flex justify-between items-center">
            <h2 className="text-[#212121] text-base font-medium">Shelf ID</h2>
            {isShelfVerified && (
              <button
                onClick={resetShelf}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                disabled={isProcessing}
              >
                Reset Shelf
              </button>
            )}
          </div>

          {isShelfVerified ? (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-lg font-bold text-green-800">{shelfId}</p>
            </div>
          ) : (
            <div className="mt-2">
              <input
                ref={shelfInputRef}
                type="text"
                className="w-full h-12 px-3 text-lg border-b-2 border-gray-300 focus:border-blue-500 outline-none bg-transparent placeholder-[#212121]/50"
                placeholder="Scan, paste, or type shelf ID..."
                value={shelfManualInput}
                onChange={handleShelfChange}
                onKeyDown={handleShelfKeyDown}
                onPaste={handleShelfPaste}
                disabled={isProcessing}
                autoFocus
              />
              {isProcessing && (
                <p className="mt-1 text-sm text-blue-600 animate-pulse">
                  Verifying shelf...
                </p>
              )}
            </div>
          )}
        </div>

        {/* AWB Section */}
        <div className="bg-[#FCFDF8] rounded p-2 space-y-2">
          <div className="flex justify-between items-center">
            <h2 className="text-[#212121] text-base font-medium">
              Shipments ({totalAwbs})
            </h2>
          </div>

          <div className="bg-[#FCFDF8] border border-[#D2D2D2] rounded-xl p-4 h-[calc(100vh-26rem)]">
            {isShelfVerified ? (
              <textarea
                id="awb-textarea"
                className="w-full h-full resize-none outline-none bg-transparent text-sm text-[#212121]/80 placeholder-[#212121]/40"
                placeholder="Paste AWBs, scan them, or type..."
                value={awbText}
                onChange={handleAwbChange}
                onPaste={handleAwbPaste}
                aria-label="Enter or scan AWB numbers"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-center">
                <p className="text-lg font-medium text-[#212121]/50">
                  Scan shelf first
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryModal;